<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import paymentMethodService, { type PaymentProvider } from '../services/payment-method.service'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const clubUuid = route.params.uuid as string
const provider = route.query.provider as PaymentProvider

const config = ref<Record<string, any>>({})
const commission = ref<number>(0)
const loading = ref(false)
const error = ref<string | null>(null)
const stripeWebhookUrl = ref<string | null>(null)
const stripeWebhookUrlLoading = ref(false)
const stripeWebhookUrlCopied = ref(false)

const pageTitle = computed(() => {
  return t('paymentMethodCreate.configTitle', { provider: paymentMethodService.getProviderName(provider) })
})

const copyStripeWebhookUrl = async () => {
  if (!stripeWebhookUrl.value) return
  await navigator.clipboard.writeText(stripeWebhookUrl.value)
  stripeWebhookUrlCopied.value = true
  setTimeout(() => { stripeWebhookUrlCopied.value = false }, 2000)
}

// Initialize config based on provider
onMounted(async () => {
  if (!provider) {
    error.value = t('paymentMethodCreate.noProvider')
    return
  }

  if (provider === 'STRIPE') {
    stripeWebhookUrlLoading.value = true
    try {
      stripeWebhookUrl.value = await paymentMethodService.getStripeWebhookUrl()
    } catch {
      // silently ignore - URL display is informational only
    } finally {
      stripeWebhookUrlLoading.value = false
    }
  }

  // Initialize empty config based on provider
  switch (provider) {
    case 'PAYU':
      config.value = {
        merchant_pos_id: '',
        client_id: '',
        client_secret: '',
        signature_key: '',
        environment: 'sandbox'
      }
      break
    case 'STRIPE':
      config.value = {
        publishable_key: '',
        secret_key: '',
        webhook_secret: '',
        environment: 'test'
      }
      break
    case 'PAYPAL':
      config.value = {
        client_id: '',
        client_secret: '',
        environment: 'sandbox'
      }
      break
    case 'PRZELEWY24':
      config.value = {
        merchant_id: '',
        pos_id: '',
        crc_key: '',
        api_key: '',
        environment: 'sandbox'
      }
      break
    case 'KLARNA':
      config.value = {
        username: '',
        password: '',
        region: 'eu',
        environment: 'playground'
      }
      break
  }
})

const handleSave = async () => {
  error.value = null

  // Validate configuration
  const validationError = paymentMethodService.validateConfiguration(provider, config.value)
  if (validationError) {
    error.value = validationError
    return
  }

  loading.value = true

  try {
    await paymentMethodService.createPaymentMethod(clubUuid, {
      provider,
      configuration: config.value,
      commission: commission.value
    })

    router.push(`/club/${clubUuid}/payment-settings`)
  } catch (err: any) {
    console.error('Error creating payment method:', err)
    error.value = err.response?.data?.message || t('paymentMethodCreate.errorSaving')
  } finally {
    loading.value = false
  }
}

const handleCancel = () => {
  router.push(`/club/${clubUuid}/payment-settings`)
}
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-6">
      <!-- Back Button -->
      <div class="mb-4">
        <button
          @click="handleCancel"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('paymentMethodCreate.backToSettings') }}
        </button>
      </div>

      <!-- Page Header -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-900">{{ pageTitle }}</h1>
        <p class="text-sm text-gray-600 mt-1">
          {{ t('paymentMethodCreate.configSubtitle', { provider: paymentMethodService.getProviderName(provider) }) }}
        </p>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        <p class="text-sm">{{ error }}</p>
      </div>

      <!-- Form -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <form @submit.prevent="handleSave" class="space-y-6">
          <!-- Commission Field -->
          <div>
            <label for="commission" class="block text-sm font-medium text-gray-700 mb-2">
              {{ t('paymentMethodCreate.commission') }}
            </label>
            <input
              id="commission"
              v-model.number="commission"
              type="number"
              min="0"
              max="100"
              step="0.01"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p class="mt-1 text-xs text-gray-600">
              {{ t('paymentMethodCreate.commissionHint') }}
            </p>
          </div>

          <!-- PayU Configuration -->
          <template v-if="provider === 'PAYU'">
            <div>
              <label for="merchant_pos_id" class="block text-sm font-medium text-gray-700 mb-2">
                Merchant POS ID <span class="text-red-600">*</span>
              </label>
              <input
                id="merchant_pos_id"
                v-model="config.merchant_pos_id"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                :placeholder="t('paymentMethodCreate.payuMerchantPosIdHint')"
              />
            </div>

            <div>
              <label for="client_id" class="block text-sm font-medium text-gray-700 mb-2">
                Client ID (OAuth) <span class="text-red-600">*</span>
              </label>
              <input
                id="client_id"
                v-model="config.client_id"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                :placeholder="t('paymentMethodCreate.payuAccessKeysHint')"
              />
            </div>

            <div>
              <label for="client_secret" class="block text-sm font-medium text-gray-700 mb-2">
                Client Secret (OAuth) <span class="text-red-600">*</span>
              </label>
              <input
                id="client_secret"
                v-model="config.client_secret"
                type="password"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                :placeholder="t('paymentMethodCreate.payuAccessKeysHint')"
              />
            </div>

            <div>
              <label for="signature_key" class="block text-sm font-medium text-gray-700 mb-2">
                Signature Key (MD5) <span class="text-red-600">*</span>
              </label>
              <input
                id="signature_key"
                v-model="config.signature_key"
                type="password"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                :placeholder="t('paymentMethodCreate.payuAccessKeysHint')"
              />
            </div>

            <div>
              <label for="environment" class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('paymentMethodCreate.environment') }} <span class="text-red-600">*</span>
              </label>
              <select
                id="environment"
                v-model="config.environment"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="sandbox">{{ t('paymentMethodCreate.sandboxTest') }}</option>
                <option value="production">{{ t('paymentMethodCreate.productionLive') }}</option>
              </select>
            </div>
          </template>

          <!-- Stripe Configuration -->
          <template v-if="provider === 'STRIPE'">
            <div>
              <label for="publishable_key" class="block text-sm font-medium text-gray-700 mb-2">
                Publishable Key <span class="text-red-600">*</span>
              </label>
              <input
                id="publishable_key"
                v-model="config.publishable_key"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Stripe Dashboard → Developers → API keys"
              />
            </div>

            <div>
              <label for="secret_key" class="block text-sm font-medium text-gray-700 mb-2">
                Secret Key <span class="text-red-600">*</span>
              </label>
              <input
                id="secret_key"
                v-model="config.secret_key"
                type="password"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Stripe Dashboard → Developers → API keys"
              />
            </div>

            <div>
              <label for="webhook_secret" class="block text-sm font-medium text-gray-700 mb-2">
                Webhook Secret <span class="text-red-600">*</span>
              </label>
              <input
                id="webhook_secret"
                v-model="config.webhook_secret"
                type="password"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Stripe Dashboard → Developers → Webhooks"
              />
              <!-- Stripe webhook setup instructions -->
              <div class="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p class="text-sm font-medium text-blue-900 mb-1">{{ t('paymentMethodCreate.stripeWebhookTitle') }}</p>
                <p class="text-xs text-blue-800 mb-3">{{ t('paymentMethodCreate.stripeWebhookDescription') }}</p>
                <div class="mb-3">
                  <p class="text-xs font-medium text-blue-900 mb-1">{{ t('paymentMethodCreate.stripeWebhookUrl') }}</p>
                  <div class="flex items-center gap-2">
                    <code v-if="stripeWebhookUrl" class="flex-1 text-xs bg-white border border-blue-200 rounded px-2 py-1.5 text-gray-800 break-all">{{ stripeWebhookUrl }}</code>
                    <span v-else class="flex-1 text-xs text-blue-600 italic">{{ stripeWebhookUrlLoading ? t('paymentMethodCreate.stripeWebhookUrlLoading') : '—' }}</span>
                    <button
                      v-if="stripeWebhookUrl"
                      type="button"
                      @click="copyStripeWebhookUrl"
                      class="shrink-0 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      {{ stripeWebhookUrlCopied ? t('paymentMethodCreate.stripeWebhookUrlCopied') : t('paymentMethodCreate.stripeWebhookUrlCopy') }}
                    </button>
                  </div>
                </div>
                <div>
                  <p class="text-xs font-medium text-blue-900 mb-1">{{ t('paymentMethodCreate.stripeWebhookEvents') }}</p>
                  <ul class="text-xs text-blue-800 space-y-0.5 list-disc list-inside">
                    <li>payment_intent.succeeded</li>
                    <li>payment_intent.payment_failed</li>
                    <li>payment_intent.canceled</li>
                    <li>checkout.session.completed</li>
                    <li>checkout.session.expired</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <label for="environment" class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('paymentMethodCreate.environment') }} <span class="text-red-600">*</span>
              </label>
              <select
                id="environment"
                v-model="config.environment"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="test">{{ t('paymentMethodCreate.testEnv') }}</option>
                <option value="live">{{ t('paymentMethodCreate.liveEnv') }}</option>
              </select>
            </div>
          </template>

          <!-- PayPal Configuration -->
          <template v-if="provider === 'PAYPAL'">
            <div>
              <label for="client_id" class="block text-sm font-medium text-gray-700 mb-2">
                Client ID <span class="text-red-600">*</span>
              </label>
              <input
                id="client_id"
                v-model="config.client_id"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="PayPal Developer Dashboard → My Apps & Credentials"
              />
            </div>

            <div>
              <label for="client_secret" class="block text-sm font-medium text-gray-700 mb-2">
                Client Secret <span class="text-red-600">*</span>
              </label>
              <input
                id="client_secret"
                v-model="config.client_secret"
                type="password"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="PayPal Developer Dashboard → My Apps & Credentials"
              />
            </div>

            <div>
              <label for="environment" class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('paymentMethodCreate.environment') }} <span class="text-red-600">*</span>
              </label>
              <select
                id="environment"
                v-model="config.environment"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="sandbox">{{ t('paymentMethodCreate.sandboxTest') }}</option>
                <option value="live">{{ t('paymentMethodCreate.liveEnv') }}</option>
              </select>
            </div>
          </template>

          <!-- Przelewy24 Configuration -->
          <template v-if="provider === 'PRZELEWY24'">
            <div>
              <label for="merchant_id" class="block text-sm font-medium text-gray-700 mb-2">
                Merchant ID <span class="text-red-600">*</span>
              </label>
              <input
                id="merchant_id"
                v-model="config.merchant_id"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                :placeholder="t('paymentMethodCreate.p24BasicDataHint')"
              />
            </div>

            <div>
              <label for="pos_id" class="block text-sm font-medium text-gray-700 mb-2">
                POS ID <span class="text-red-600">*</span>
              </label>
              <input
                id="pos_id"
                v-model="config.pos_id"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                :placeholder="t('paymentMethodCreate.p24BasicDataHint')"
              />
            </div>

            <div>
              <label for="crc_key" class="block text-sm font-medium text-gray-700 mb-2">
                CRC Key <span class="text-red-600">*</span>
              </label>
              <input
                id="crc_key"
                v-model="config.crc_key"
                type="password"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                :placeholder="t('paymentMethodCreate.p24SettingsHint')"
              />
            </div>

            <div>
              <label for="api_key" class="block text-sm font-medium text-gray-700 mb-2">
                API Key <span class="text-red-600">*</span>
              </label>
              <input
                id="api_key"
                v-model="config.api_key"
                type="password"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                :placeholder="t('paymentMethodCreate.p24ApiHint')"
              />
            </div>

            <div>
              <label for="environment" class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('paymentMethodCreate.environment') }} <span class="text-red-600">*</span>
              </label>
              <select
                id="environment"
                v-model="config.environment"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="sandbox">{{ t('paymentMethodCreate.sandboxTest') }}</option>
                <option value="production">{{ t('paymentMethodCreate.productionLive') }}</option>
              </select>
            </div>
          </template>

          <!-- Klarna Configuration -->
          <template v-if="provider === 'KLARNA'">
            <div>
              <label for="username" class="block text-sm font-medium text-gray-700 mb-2">
                API Username <span class="text-red-600">*</span>
              </label>
              <input
                id="username"
                v-model="config.username"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Klarna Merchant Portal → Settings → API Credentials"
              />
            </div>

            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
                API Password <span class="text-red-600">*</span>
              </label>
              <input
                id="password"
                v-model="config.password"
                type="password"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Klarna Merchant Portal → Settings → API Credentials"
              />
            </div>

            <div>
              <label for="region" class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('paymentMethodCreate.region') }} <span class="text-red-600">*</span>
              </label>
              <select
                id="region"
                v-model="config.region"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="eu">{{ t('paymentMethodCreate.europeRegion') }}</option>
                <option value="na">{{ t('paymentMethodCreate.northAmericaRegion') }}</option>
                <option value="oc">{{ t('paymentMethodCreate.oceaniaRegion') }}</option>
              </select>
            </div>

            <div>
              <label for="environment" class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('paymentMethodCreate.environment') }} <span class="text-red-600">*</span>
              </label>
              <select
                id="environment"
                v-model="config.environment"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="playground">{{ t('paymentMethodCreate.playgroundEnv') }}</option>
                <option value="production">{{ t('paymentMethodCreate.productionLive') }}</option>
              </select>
            </div>
          </template>

          <!-- Action Buttons -->
          <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              @click="handleCancel"
              :disabled="loading"
              class="px-6 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="px-6 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ loading ? t('paymentMethodCreate.saving') : t('paymentMethodCreate.saveConfig') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>
